import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, Save, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminPwaSettings() {
  const [percent, setPercent] = useState(80);
  const [saved, setSaved] = useState(80);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/pwa/config")
      .then(({ data }) => { setPercent(data.icon_percent); setSaved(data.icon_percent); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/pwa/config", { icon_percent: percent });
      setSaved(percent);
      toast.success("Taille de l'icône PWA enregistrée ✅ (réinstallez l'app pour l'appliquer)");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const src = (size, maskable) =>
    `${BACKEND}/api/pwa/icon.png?size=${size}${maskable ? "&maskable=1" : ""}&percent=${percent}`;

  if (loading) return <div className="p-6 text-slate-500">Chargement…</div>;

  return (
    <div data-testid="admin-pwa-page" className="max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-white">
          <Smartphone className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Icône de l'application (PWA)</h1>
          <p className="mt-0.5 text-sm text-slate-500">Réglez la taille du logo dans l'icône installée, de 0 % à 100 %.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-white p-6">
        <div className="flex flex-wrap items-center gap-8">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-3xl bg-slate-900 p-2 ring-1 ring-black/10" style={{ background: "#060B1A" }}>
              <img key={`std-${percent}`} src={src(192, false)} alt="Aperçu icône" className="h-32 w-32 rounded-2xl" data-testid="pwa-preview-standard" />
            </div>
            <span className="text-[11px] font-bold text-slate-400">Standard</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <img key={`msk-${percent}`} src={src(192, true)} alt="Aperçu masquable" className="h-32 w-32 rounded-full ring-1 ring-black/10" data-testid="pwa-preview-maskable" />
            <span className="text-[11px] font-bold text-slate-400">Masquable (Android)</span>
          </div>

          <div className="min-w-[240px] flex-1">
            <div className="mb-2 flex items-end justify-between">
              <label className="text-sm font-bold text-ink">Taille du logo</label>
              <span className="font-display text-3xl font-extrabold text-brand" data-testid="pwa-percent-value">{percent}%</span>
            </div>
            <input
              type="range" min="0" max="100" step="1" value={percent}
              onChange={(e) => setPercent(Number(e.target.value))}
              data-testid="pwa-percent-slider"
              className="w-full accent-brand"
            />
            <div className="mt-1 flex justify-between text-[11px] text-slate-400">
              <span>0 %</span><span>Recommandé : 80 %</span><span>100 %</span>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={save} disabled={busy || percent === saved}
                data-testid="pwa-save-btn"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-white transition hover:bg-black disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </button>
              {percent !== saved && <span className="text-xs font-semibold text-amber-600">Modification non enregistrée</span>}
            </div>
          </div>
        </div>

        <p className="mt-6 rounded-xl bg-slate-50 p-3 text-[12px] leading-relaxed text-slate-500">
          L'aperçu est mis à jour en direct. Après enregistrement, les nouvelles icônes sont servies à tous.
          Sur un téléphone où l'app est déjà installée, désinstallez puis réinstallez pour voir le changement.
        </p>
      </div>
    </div>
  );
}
